import type CanopyConnectComponents from '../components/index.js';

export type { CanopyConnectComponents };

let componentsPromise: Promise<CanopyConnectComponents> | undefined;

export function loadCanopyConnectComponents(): Promise<CanopyConnectComponents> {
  return getComponentsPromise();
}

function getComponentsPromise(): Promise<CanopyConnectComponents> {
  if (componentsPromise !== undefined) {
    return componentsPromise;
  }

  componentsPromise = new Promise((resolve, reject) => {
    loadScript(
      () => {
        if ('CanopyConnectComponents' in window) {
          resolve(window.CanopyConnectComponents as CanopyConnectComponents);
        } else {
          reject(new Error('Failed to initialize Canopy Connect Components script'));
        }
      },
      (cause?: unknown) => {
        componentsPromise = undefined; // Allow retrying

        reject(
          new Error(
            'Failed to load Canopy Connect Components script',
            // @ts-expect-error - The `cause` property is not available in our target
            // environment, but it is safe to pass for those that do support it.
            { cause }
          )
        );
      }
    );
  });

  return componentsPromise;
}

function loadScript(onLoad: () => void, onError: (cause?: unknown) => void): void {
  const script = document.createElement('script');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- injected by webpack
  script.src = process.env.COMPONENTS_URL!;
  script.async = true;
  script.fetchPriority = 'high';

  script.addEventListener('load', onLoadListener, { once: true });
  script.addEventListener('error', onErrorListener, { once: true });

  const headOrBody = document.head || document.body;
  if (!headOrBody) {
    throw new Error('Failed to find <head> or <body> element to append script');
  }
  headOrBody.appendChild(script);

  function onLoadListener() {
    script.removeEventListener('error', onErrorListener);
    onLoad();
  }

  function onErrorListener(event: ErrorEvent) {
    script.removeEventListener('load', onLoadListener);
    onError(event.error);
  }
}
